import React from "react";
import "./SessionDetailPanel.css";
import { SidePanel } from "./SidePanel";
import { Button } from "./FormField";
import { Session } from "../../types";

interface SessionDetailPanelProps {
  session: Session | null;
  onClose: () => void;
  onPracticeAgain?: (scenarioId: string) => void;
}

export function SessionDetailPanel({ session, onClose, onPracticeAgain }: SessionDetailPanelProps) {
  if (!session) return null;

  const score = session.score;
  const scoreClass = score
    ? score.overallScore >= 80 ? "--high" : score.overallScore >= 60 ? "--mid" : "--low"
    : "";

  const formatDate = (iso: string) => new Date(iso).toLocaleDateString();
  const formatDuration = (ms: number) => {
    if (!ms) return "—";
    const mins = Math.floor(ms / 60000);
    const secs = Math.floor((ms % 60000) / 1000);
    return `${mins}m ${secs}s`;
  };

  return (
    <SidePanel
      isOpen
      onClose={onClose}
      title="Session Detail"
      width="560px"
      footer={
        onPracticeAgain ? (
          <Button onClick={() => onPracticeAgain(session.scenarioId)}>🎭 Practice Again</Button>
        ) : undefined
      }
    >
      <div className="protrain-session-detail-meta">
        <span>{session.mode === "text" ? "💬" : session.mode === "transcript" ? "📄" : "🎤"} {session.mode}</span>
        <span>{session.personaType}</span>
        <span>{formatDate(session.createdAt)}</span>
        <span>{formatDuration(session.durationMs)}</span>
      </div>

      {score && (
        <>
          <div className="protrain-session-detail-score">
            <span className={`protrain-session-detail-score-value protrain-session-detail-score-value${scoreClass}`}>
              {score.overallScore}/100
            </span>
            <span className="protrain-session-detail-score-label">Overall Score</span>
          </div>

          {score.kpiScores.length > 0 && (
            <div className="protrain-session-detail-section">
              <h4>KPI Scores</h4>
              <div className="protrain-session-detail-kpis">
                {score.kpiScores.map((kpi, i) => (
                  <div key={i} className="protrain-session-detail-kpi">
                    <div>
                      <span className="protrain-session-detail-kpi-name">{kpi.kpiName}</span>
                      {kpi.feedback && <div className="protrain-session-detail-kpi-feedback">{kpi.feedback}</div>}
                    </div>
                    <span className="protrain-session-detail-kpi-score">{kpi.score}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {score.strengthAreas.length > 0 && (
            <div className="protrain-session-detail-section">
              <h4>Strengths</h4>
              <div className="protrain-session-detail-strengths">
                {score.strengthAreas.map((s, i) => (
                  <span key={i} className="protrain-session-detail-strength">{s}</span>
                ))}
              </div>
            </div>
          )}

          {score.improvementAreas.length > 0 && (
            <div className="protrain-session-detail-section">
              <h4>Improvement Areas</h4>
              <div className="protrain-session-detail-areas">
                {score.improvementAreas.map((area, i) => (
                  <div key={i} className="protrain-session-detail-area">
                    <span className="protrain-session-detail-area-name">{area.area}</span>
                    <div className="protrain-session-detail-area-desc">{area.description}</div>
                    <span className={`protrain-session-detail-area-priority protrain-session-detail-area-priority--${area.priority}`}>
                      {area.priority}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {score.redFlagsDetected.length > 0 && (
            <div className="protrain-session-detail-section">
              <h4>Red Flags</h4>
              <div className="protrain-session-detail-redflags">
                {score.redFlagsDetected.map((flag, i) => (
                  <div key={i} className="protrain-session-detail-redflag">
                    <span className="protrain-session-detail-redflag-phrase">"{flag.phrase}"</span>
                    <div className="protrain-session-detail-redflag-context">{flag.context}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      <div className="protrain-session-detail-section">
        <h4>Transcript</h4>
        {session.transcript.length > 0 ? (
          <div className="protrain-session-detail-transcript">
            {session.transcript.map((entry, i) => (
              <div key={i} className={`protrain-session-detail-entry protrain-session-detail-entry--${entry.role}`}>
                <span className="protrain-session-detail-role">{entry.role === "rep" ? "You" : "Prospect"}</span>
                {entry.message}
              </div>
            ))}
          </div>
        ) : (
          <p className="protrain-session-detail-empty">No transcript available.</p>
        )}
      </div>
    </SidePanel>
  );
}
